import { jsxs, jsx } from "react/jsx-runtime";
import { useCallback } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useOrderingQuiz } from "../hooks/use-ordering-quiz.js";
import { useSensors, useSensor, DndContext, closestCenter, KeyboardSensor, PointerSensor } from "../../../node_modules/.pnpm/@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/@dnd-kit/core/dist/core.esm.js";
import { arrayMove, SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "../../../node_modules/.pnpm/@dnd-kit_sortable@8.0.0_@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1__react@18.3.1/node_modules/@dnd-kit/sortable/dist/sortable.esm.js";
import { CSS } from "../../../node_modules/.pnpm/@dnd-kit_utilities@3.2.2_react@18.3.1/node_modules/@dnd-kit/utilities/dist/utilities.esm.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import ArrowRight from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/arrow-right.js";
const restrictToHorizontal = ({ transform }) => ({
  ...transform,
  y: 0
});
const restrictToVertical = ({ transform }) => ({
  ...transform,
  x: 0
});
function SortableItem({ id, answer, index, isCorrect, isSolved }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : "auto"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setNodeRef,
      style,
      className: cn(
        "relative flex items-center gap-3 p-4 rounded-xl border-2 bg-white transition-all",
        isDragging && "border-blue-400 bg-blue-50 shadow-lg z-10",
        !isDragging && !isSolved && "border-gray-200 hover:border-blue-300 hover:shadow-2xs cursor-grab",
        isSolved && isCorrect === true && "border-green-300 bg-green-50",
        isSolved && isCorrect === false && "border-red-300 bg-red-50",
        isSolved && "pointer-events-none"
      ),
      ...attributes,
      ...listeners,
      children: [
        /* @__PURE__ */ jsx("div", { className: cn(
          "shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
          isDragging && "bg-blue-500 text-white",
          !isDragging && !isSolved && "bg-blue-500 text-white",
          isSolved && isCorrect === true && "bg-green-500 text-white",
          isSolved && isCorrect === false && "bg-red-500 text-white"
        ), children: index + 1 }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          answer.image && /* @__PURE__ */ jsx(
            "img",
            {
              src: answer.image.signedUrl || answer.image.url,
              alt: `item-${index}`,
              className: "max-h-20 w-auto object-contain"
            }
          ),
          answer.answer && /* @__PURE__ */ jsx("span", { className: "text-lg font-medium text-gray-800", children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer }) })
        ] })
      ]
    }
  );
}
function OrderingAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit
  } = useOrderingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedChoices((prev) => {
      const oldIndex = prev.findIndex((c) => c.key === active.id);
      const newIndex = prev.findIndex((c) => c.key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, [setOrderedChoices]);
  const isDisabled = isSolved || isViewSolved;
  const getArrowColor = () => {
    if (!isSolved) return "text-blue-400";
    if ((solveResult == null ? void 0 : solveResult.correct) || shouldShowCorrectAnswers) return "text-green-500";
    return "text-red-500";
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
          /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-blue-500 rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.ordering.label") }) }),
          /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
          /* @__PURE__ */ jsx(DebugQuizId, { quizId })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    !isDisabled && /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-500", children: t("types.ordering.dragInstruction") }),
    /* @__PURE__ */ jsx(
      DndContext,
      {
        sensors,
        collisionDetection: closestCenter,
        onDragEnd: handleDragEnd,
        modifiers: isVertical ? [restrictToVertical] : [restrictToHorizontal],
        children: /* @__PURE__ */ jsx(
          SortableContext,
          {
            items: orderedChoices.map((c) => c.key),
            strategy: isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy,
            children: /* @__PURE__ */ jsx("div", { className: cn(
              isVertical ? "flex flex-col items-stretch space-y-3" : "flex flex-row items-center justify-between space-x-4"
            ), children: orderedChoices.map((choice, index) => {
              const isCorrect = isSolved ? shouldShowCorrectAnswers || ((solveResult == null ? void 0 : solveResult.correct) ?? false) : null;
              return /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "relative",
                  style: !isVertical ? { width: `${100 / orderedChoices.length}%` } : void 0,
                  children: [
                    index > 0 && /* @__PURE__ */ jsx(
                      ArrowRight,
                      {
                        strokeWidth: 2.5,
                        className: cn(
                          "absolute w-5 h-5",
                          getArrowColor(),
                          isVertical ? "left-1/2 -translate-x-1/2 -top-4 rotate-90" : "-left-3 top-1/2 -translate-y-1/2"
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      SortableItem,
                      {
                        id: choice.key,
                        answer: choice,
                        index,
                        isCorrect,
                        isSolved
                      }
                    )
                  ]
                },
                choice.key
              );
            }) })
          }
        )
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers && (solveResult == null ? void 0 : solveResult.correctAnswers) ? solveResult.correctAnswers.map((key) => {
          var _a;
          return (_a = quiz.choices.find((c) => c.key === key)) == null ? void 0 : _a.answer;
        }).filter(Boolean).join(" → ") : void 0,
        disableSound
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !(solveResult == null ? void 0 : solveResult.correct),
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  OrderingAdult
};
//# sourceMappingURL=ordering-adult.js.map
